/**
 * Where Settings' URLs lead (PR 7: A8 + owner decision 6, "Settings stays ONE
 * sidebar item"). Settings is one page at /ops that opens on the real
 * settings tabs; the engineering tabs that used to open it (System health,
 * notifications, sessions) are admin tabs after them.
 *
 * Pure functions, no React, so routing.test.ts can walk the whole redirect
 * table and OpsHub.tsx stays a thin shell. Modeled on pages/automations/
 * routing.ts. Nothing here fetches or writes.
 *
 * The old addresses and where they go:
 *   /ops, /ops?tab=<missing|unknown>          -> /ops?tab=users
 *   /ops?tab=overview|health|freshness|ops-health -> /ops?tab=system
 *   /ops?tab=settings (the old Settings tab)  -> /ops?tab=integrations
 *   /ops?tab=engine                           -> /ops?tab=advanced&section=engine
 *   /ops?tab=scheduler                        -> /ops?tab=advanced&section=scheduler
 *   /ops?tab=advanced&section=<writes|unknown> -> /ops?tab=advanced
 *   /ops?tab=<not advanced>&section=…         -> the same tab, section dropped
 * (The /settings, /settings/v6, /settings/users and /settings/advanced routes
 * are <Navigate>s in AppRoutes.tsx onto these.)
 *
 * The canonical URL depends ONLY on the URL, never on who is looking: while
 * /api/auth/me is still loading isAdmin reads false, and an admin's deep link
 * must not be rewritten to Users in that window. A non-admin asking for an
 * admin tab keeps their URL; the page shows Users with a one-line notice.
 */

export const SETTINGS_PATH = '/ops'

export type SettingsTab =
  | 'users' | 'integrations' | 'automation-mode' | 'display' | 'advanced'
  | 'feedback' | 'system' | 'notifications' | 'sessions'

export type AdvancedSection = 'writes' | 'engine' | 'scheduler'

export type SettingsTabMeta = {
  id: SettingsTab
  label: string
  group: 'settings' | 'admin'
  adminOnly: boolean
}

export const DEFAULT_TAB: SettingsTab = 'users'
export const DEFAULT_SECTION: AdvancedSection = 'writes'

// Strip order. The settings group comes first because that is what the page
// is for; the admin group is the engineering view, after a divider.
export const SETTINGS_TABS: ReadonlyArray<SettingsTabMeta> = [
  { id: 'users',           label: 'Users',                group: 'settings', adminOnly: false },
  { id: 'integrations',    label: 'Integrations',         group: 'settings', adminOnly: false },
  { id: 'automation-mode', label: 'Automation mode',      group: 'settings', adminOnly: false },
  { id: 'display',         label: 'Display',              group: 'settings', adminOnly: false },
  { id: 'advanced',        label: 'Advanced',             group: 'settings', adminOnly: true  },
  { id: 'feedback',        label: 'Feedback',             group: 'admin',    adminOnly: true  },
  { id: 'system',          label: 'System health',        group: 'admin',    adminOnly: true  },
  { id: 'notifications',   label: 'System notifications', group: 'admin',    adminOnly: true  },
  { id: 'sessions',        label: 'Sessions',             group: 'admin',    adminOnly: true  },
]

/** The sections inside Advanced, in strip order. Writes is the default and
 *  is never written into the URL, so /ops?tab=advanced stays canonical. */
export const ADVANCED_SECTIONS: ReadonlyArray<{ id: AdvancedSection; label: string }> = [
  { id: 'writes',    label: 'Amazon writes' },
  { id: 'engine',    label: 'Engine' },
  { id: 'scheduler', label: 'Scheduler' },
]

const TAB_BY_ID: ReadonlyMap<string, SettingsTabMeta> = new Map(SETTINGS_TABS.map(t => [t.id, t]))
const SECTION_IDS: ReadonlySet<string> = new Set(ADVANCED_SECTIONS.map(s => s.id))

// Retired ?tab= values and where each one lives now. 'feedback' is not here:
// it was an alias for Users and is a real (admin) tab again.
const TAB_ALIASES: Readonly<Record<string, { tab: SettingsTab; section?: AdvancedSection }>> = {
  // The four monitoring tabs merged into one System health page (2026-07-03).
  overview: { tab: 'system' },
  health: { tab: 'system' },
  freshness: { tab: 'system' },
  'ops-health': { tab: 'system' },
  // The old "Settings" tab: its first real section was Integrations.
  settings: { tab: 'integrations' },
  // Engine and the scheduler list are sections of Advanced now (D3).
  engine: { tab: 'advanced', section: 'engine' },
  scheduler: { tab: 'advanced', section: 'scheduler' },
}

/** The display label of a tab. */
export function tabLabel(tab: SettingsTab): string {
  return TAB_BY_ID.get(tab)?.label ?? tab
}

/** The tabs a person sees in the strip. CLICKPAY_AUTH=off reads as admin
 *  (useAuth), so with auth off every tab shows, as before. */
export function visibleTabs(isAdmin: boolean): ReadonlyArray<SettingsTabMeta> {
  return SETTINGS_TABS.filter(t => isAdmin || !t.adminOnly)
}

/** A link to one tab (and, for Advanced, one section). */
export function settingsHref(tab: SettingsTab, section?: AdvancedSection): string {
  if (tab === 'advanced' && section && section !== DEFAULT_SECTION) {
    return `${SETTINGS_PATH}?tab=advanced&section=${section}`
  }
  return `${SETTINGS_PATH}?tab=${tab}`
}

export type ResolvedSettingsUrl = {
  /** What the URL asks for, after aliases. */
  tab: SettingsTab
  /** Only when tab === 'advanced'. */
  section: AdvancedSection | null
  /** What renders: DEFAULT_TAB when the tab is denied. */
  shown: SettingsTab
  /** An admin tab requested by a non-admin. */
  denied: SettingsTab | null
  /** Replace the URL's query with this; null when it is already canonical. */
  canonicalSearch: string | null
}

export function resolveSettingsUrl(search: string, isAdmin: boolean): ResolvedSettingsUrl {
  const params = new URLSearchParams(search)
  const raw = params.get('tab')
  const alias = raw !== null && Object.prototype.hasOwnProperty.call(TAB_ALIASES, raw) ? TAB_ALIASES[raw] : null

  let tab: SettingsTab
  let section: AdvancedSection | null = null
  if (alias) {
    tab = alias.tab
    section = alias.section ?? null
  } else if (raw !== null && TAB_BY_ID.has(raw)) {
    tab = raw as SettingsTab
  } else {
    tab = DEFAULT_TAB
  }
  if (tab === 'advanced') {
    if (section === null) {
      const rawSection = params.get('section')
      section = rawSection !== null && SECTION_IDS.has(rawSection) ? (rawSection as AdvancedSection) : DEFAULT_SECTION
    }
  } else {
    section = null
  }

  const denied = TAB_BY_ID.get(tab)?.adminOnly && !isAdmin ? tab : null
  const shown = denied ? DEFAULT_TAB : tab

  // Already canonical: exactly one tab param naming the resolved tab, and a
  // section param only when it says something other than the default.
  const wantSection = section !== null && section !== DEFAULT_SECTION ? section : null
  const sections = params.getAll('section')
  const canonical =
    params.getAll('tab').length === 1 && raw === tab
    && (wantSection === null ? sections.length === 0 : sections.length === 1 && sections[0] === wantSection)

  return {
    tab, section, shown, denied,
    canonicalSearch: canonical ? null : withTab(search, tab, section ?? undefined),
  }
}

/**
 * The query string after a click on a tab (or an Advanced section): tab set,
 * section set only for a non-default Advanced section, every other parameter
 * kept as it was. No leading `?`.
 */
export function withTab(search: string, tab: SettingsTab, section?: AdvancedSection): string {
  const next = new URLSearchParams(search)
  next.set('tab', tab)
  if (tab === 'advanced' && section && section !== DEFAULT_SECTION) next.set('section', section)
  else next.delete('section')
  return next.toString()
}
